import {
  DashboardLeagueListItem,
  LeagueGeoScope as RestLeagueGeoScope,
  LeagueRef,
  LeagueStatus as RestLeagueStatus,
} from "@/api/__generated__/public/schemas";
import {
  DashboardCreateLeagueMemberInputRolesItem,
  DashboardLeagueAthleteListItem,
  DashboardLeagueTeamListItem,
  DashboardLeagueDetail,
  DashboardLeagueMemberListItem,
  DashboardLeagueMemberListItemRolesItem,
  DashboardLeagueMembersControllerListRolesItem,
  DashboardUpdateLeagueMemberInputRolesItem,
  DashboardLeagueFactSheetItem,
  DashboardLeaguesControllerListStatus,
  DashboardLeagueOnboardingStep,
  DashboardLeagueOverview,
  DashboardLeagueOverviewAlbum,
  DashboardLeagueOverviewDocument,
  DashboardLeagueOverviewNews,
  DashboardLeagueOverviewPartner,
  DashboardPlatformFee,
  FederationRef,
  LeagueRef as DashboardLeagueRef,
  ScopePair as DashboardScopePair,
  SportRef as ApiSportRef,
  LeagueGeoScope as DashboardLeagueGeoScope,
  LeagueRatingSystems as DashboardLeagueRatingSystems,
  LeagueSocialLinkInput as DashboardLeagueSocialLinkInput,
  LeagueStatus as DashboardLeagueStatus,
  SocialMediaType as DashboardSocialMediaType,
  SporfieMode,
  TourType,
  TourType as DashboardTourType,
} from "@/api/__generated__/schemas";
import {
  LeagueCategoryFragment,
  LeagueCategoryWithLeagueFragment,
  LeagueFragment,
  LeagueGeoScope as ApiLeagueGeoScope,
  LeagueMemberFragment,
  LeagueMemberWithLeagueFragment,
  LeagueRankingFragment,
  LeagueSocialLinkInput,
  LeagueWithOwnerFragment,
  LeagueWithPlatformsFragment,
  LeagueWithSocialLinksFragment,
  LeagueWithSportsFragment,
  LeagueWithTournamentsCountFragment,
  SocialMediaType as ApiSocialMediaType,
  fromApiCalendarDateNullable,
  fromApiDate,
} from "@/graphql";
import {
  mapToTaggedContentSettings,
  WithTaggedContentSettings,
} from "@/mappers/contentLink.mappers";
import {
  League,
  LeagueAthleteListItem,
  LeagueTeamListItem,
  LeagueManagementMember,
  LeagueMemberRoleName,
  LeagueCategory,
  LeagueDetail,
  LeagueFactSheetField,
  LeagueFactSheetFieldKind,
  LeagueCategoryWithLeague,
  LeagueListItem,
  LeagueMember,
  LeagueMemberWithLeague,
  LeagueGeoScope,
  LeagueNaming,
  LeagueTourType,
  LeagueOnboardingStepState,
  LeagueOverview,
  LeagueOverviewAlbum,
  LeagueOverviewDocument,
  LeagueOverviewNews,
  LeagueOverviewPartner,
  LeagueRanking,
  LeagueRatingSystem,
  LeagueRatingSystemAvailability,
  LeagueRatingSystems,
  LeagueReference,
  LeagueSearchOption,
  LeagueSporfieMode,
  LeagueStatus,
  LeagueWithOwner,
  LeagueWithPlatforms,
  LeagueWithDocuments,
  LeagueWithSocialLinks,
  LeagueWithSports,
  LeagueWithTournamentsCount,
  SocialLink,
  SocialMediaType,
  User,
} from "@/types";

import { mapToCapabilitySet } from "./capability.mappers";
import { mapToBaseCategory, mapToCategoryRule } from "./category.mappers";
import { mapToClubReference, mapToClubReferenceFromRef } from "./club.mappers";
import { mapAvatarFields, mapToCountryCodeNullable } from "./common.mappers";
import { mapToDocumentsAndFoldersFromApi } from "./document.mappers";
import { mapToOrganization, mapToOrganizationReference } from "./organization.mappers";
import {
  mapToFeesSegment,
  mapToInheritedTournatedFeeVat,
  mapToTournatedFeeVat,
} from "./payment.mappers";
import { mapToBasePlatform, mapToPlatformReference } from "./platform.mappers";
import { mapToScopePair } from "./scope.mappers";
import { mapToSport, mapToSportRef } from "./sport.mappers";
import { mapToTeam } from "./team.mappers";
import { mapToRankingTournamentCategory } from "./tournament.mappers";
import { mapToUser } from "./user.mappers";

export const mapToLeague = (fragment: LeagueFragment): League => ({
  id: String(fragment.id),
  title: fragment.title ?? "",
  type: mapToLeagueGeoScope(fragment.type),
  status: mapToLeagueStatus(fragment.status!),
  email: fragment.email ?? null,
  website: fragment.website ?? null,
  logo: fragment.logo ?? null,
  bannerCoverUrl: fragment.bannerCover ?? null,
  regulationsUrl: fragment.abbreviationLogo ?? null,
  country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
});

export const mapToLeagueWithSports = (fragment: LeagueWithSportsFragment): LeagueWithSports => ({
  sport: fragment.sports ? mapToSport(fragment.sports) : null,
});

export const mapToLeagueWithPlatforms = (
  fragment: LeagueWithPlatformsFragment
): LeagueWithPlatforms => ({
  platforms: fragment.platforms?.map(mapToBasePlatform) ?? [],
});

export const mapToLeagueWithOwner = (fragment: LeagueWithOwnerFragment): LeagueWithOwner => ({
  owner: fragment?.federation ? mapToOrganization(fragment.federation) : null,
});

const API_TO_DOMAIN_SOCIAL_MEDIA_TYPE: { [key in ApiSocialMediaType]: SocialMediaType } = {
  [ApiSocialMediaType.Instagram]: "instagram",
  [ApiSocialMediaType.Facebook]: "facebook",
  [ApiSocialMediaType.Tiktok]: "tiktok",
  [ApiSocialMediaType.Youtube]: "youtube",
  [ApiSocialMediaType.Linkedin]: "linkedin",
  [ApiSocialMediaType.Discord]: "discord",
  [ApiSocialMediaType.Twitch]: "twitch",
};

export const mapToLeagueWithSocialLinks = (
  fragment: LeagueWithSocialLinksFragment
): LeagueWithSocialLinks => ({
  socialLinks: (fragment.socialLinks ?? []).map((link) => ({
    socialMediaType: API_TO_DOMAIN_SOCIAL_MEDIA_TYPE[link.socialMediaType],
    url: link.url,
  })),
});

export const mapToLeagueWithTournamentsCount = (
  fragment: LeagueWithTournamentsCountFragment
): LeagueWithTournamentsCount => ({
  tournamentsCount: fragment.tournamentsCount ?? 0,
});

export const mapToLeagueMemberWithLeague = (
  fragment: LeagueMemberWithLeagueFragment
): LeagueMemberWithLeague => ({
  league: {
    id: fragment.league!.id,
    title: fragment.league!.title ?? "",
  },
});

export const mapToLeagueStatus = (apiStatus: string): LeagueStatus => {
  switch (apiStatus) {
    case "Active":
      return "active";
    case "Inactive":
      return "inactive";
    default:
      return "inactive";
  }
};

export const mapToApiLeagueStatus = (status: LeagueStatus): string => {
  switch (status) {
    case "active":
      return "Active";
    case "inactive":
      return "Inactive";
  }
};

export const mapToLeagueCategory = (fragment: LeagueCategoryFragment): LeagueCategory => ({
  id: fragment.id!,
  category: {
    ...mapToBaseCategory(fragment.category!),
    categoryRules: fragment.category!.categoryRules?.map(mapToCategoryRule) ?? [],
    rankingTournamentCategories:
      fragment.category!.tournamentCategories?.map(mapToRankingTournamentCategory) ?? [],
    tournaments:
      fragment.category!.tournaments?.map((tournamentFragment) => ({
        id: String(tournamentFragment.id),
      })) ?? [],
  },
});

export const mapToLeagueCategoryWithLeague = (
  fragment: LeagueCategoryWithLeagueFragment
): LeagueCategoryWithLeague => ({
  league: {
    id: String(fragment.league!.id),
    title: fragment.league!.title ?? "",
  },
});

export const mapToLeagueRanking = (fragment: LeagueRankingFragment): LeagueRanking => ({
  id: fragment.id,
  createdAt: fromApiDate(fragment.createdAt),
  date: fragment.date!,
  updatedAt: fromApiDate(fragment.updatedAt),
  isPublic: fragment.isPublic ?? false,
  points: fragment.points ?? null,
  place: fragment.place ?? null,
  tournaments: fragment.tournaments ?? null,
  thirdPartyPoints: fragment.thirdPartyPoints ?? null,
  league: fragment.league
    ? {
        id: String(fragment.league.id),
        title: fragment.league.title ?? "",
        logo: fragment.league.logo ?? "",
      }
    : null,
  user: fragment.user
    ? (() => {
        const u = fragment.user as {
          userRatings?: User["userRatings"];
          wprCompetitionRating?: string | null;
          wprSocialRating?: string | null;
          wtnRating?: number | null;
        };
        const fromNested =
          u.userRatings?.map((r) => ({
            provider: r.provider,
            wtnRating: r.wtnRating ?? null,
            duprSinglesRating: r.duprSinglesRating ?? null,
            duprDoublesRating: r.duprDoublesRating ?? null,
            wprCompetitionRating: r.wprCompetitionRating ?? null,
            wprSocialRating: r.wprSocialRating ?? null,
          })) ?? null;
        const fromFlat: User["userRatings"] =
          !fromNested?.length &&
          (u.wprCompetitionRating != null || u.wprSocialRating != null || u.wtnRating != null)
            ? [
                ...(u.wprCompetitionRating != null || u.wprSocialRating != null
                  ? [
                      {
                        provider: "WPR",
                        wtnRating: null,
                        duprSinglesRating: null,
                        duprDoublesRating: null,
                        wprCompetitionRating: u.wprCompetitionRating ?? null,
                        wprSocialRating: u.wprSocialRating ?? null,
                      },
                    ]
                  : []),
                ...(u.wtnRating != null
                  ? [
                      {
                        provider: "WTN",
                        wtnRating: u.wtnRating ?? null,
                        duprSinglesRating: null,
                        duprDoublesRating: null,
                        wprCompetitionRating: null,
                        wprSocialRating: null,
                      },
                    ]
                  : []),
              ]
            : null;
        return {
          ...mapToUser(fragment.user),
          wprCompetitionRating: u.wprCompetitionRating ?? null,
          wprSocialRating: u.wprSocialRating ?? null,
          userRatings: fromNested?.length ? fromNested : fromFlat,
        };
      })()
    : null,
  rankingCategory: {
    id: fragment.rankingCategory!.id,
    category: mapToBaseCategory(fragment.rankingCategory!.category!),
  },
  team: fragment.team != null ? mapToTeam(fragment.team) : null,
});

export const mapToLeagueMember = (fragment: LeagueMemberFragment): LeagueMember => ({
  id: fragment.id,
  user: mapToUser(fragment.member!),
  roles:
    fragment.role?.map((roleFragment) => ({
      id: roleFragment.id,
      role: {
        id: roleFragment.role!.id,
        name: roleFragment.role!.name ?? "",
      },
    })) ?? [],
});

export const mapToLeagueGeoScope = (type: ApiLeagueGeoScope | null | undefined): LeagueGeoScope => {
  switch (type) {
    case ApiLeagueGeoScope.National:
      return "national";
    case ApiLeagueGeoScope.International:
      return "international";
    case ApiLeagueGeoScope.Local:
    default:
      return "local";
  }
};

export const mapToApiLeagueGeoScope = (type: LeagueGeoScope): ApiLeagueGeoScope => {
  switch (type) {
    case "national":
      return ApiLeagueGeoScope.National;
    case "international":
      return ApiLeagueGeoScope.International;
    case "local":
      return ApiLeagueGeoScope.Local;
  }
};

const DOMAIN_TO_API_SOCIAL_MEDIA_TYPE: { [key in SocialMediaType]: ApiSocialMediaType } = {
  instagram: ApiSocialMediaType.Instagram,
  facebook: ApiSocialMediaType.Facebook,
  tiktok: ApiSocialMediaType.Tiktok,
  youtube: ApiSocialMediaType.Youtube,
  linkedin: ApiSocialMediaType.Linkedin,
  discord: ApiSocialMediaType.Discord,
  twitch: ApiSocialMediaType.Twitch,
};

export const mapToApiLeagueSocialLinks = (links: SocialLink[]): LeagueSocialLinkInput[] =>
  links.map((link) => ({
    socialMediaType: DOMAIN_TO_API_SOCIAL_MEDIA_TYPE[link.socialMediaType],
    url: link.url,
  }));

const mapToLeagueStatusNullable = (status: RestLeagueStatus | null): LeagueStatus | null => {
  switch (status) {
    case "active":
      return "active";
    case "inactive":
      return "inactive";
    default:
      return null;
  }
};

const mapToLeagueGeoScopeNullable = (type: RestLeagueGeoScope | null): LeagueGeoScope | null => {
  switch (type) {
    case "national":
      return "national";
    case "international":
      return "international";
    case "local":
      return "local";
    default:
      return null;
  }
};

export const mapToRestApiLeagueStatus = (
  status: LeagueStatus
): DashboardLeaguesControllerListStatus => {
  switch (status) {
    case "active":
      return DashboardLeaguesControllerListStatus.active;
    case "inactive":
      return DashboardLeaguesControllerListStatus.inactive;
  }
};

/**
 * Map the domain lifecycle status to the dashboard REST create/update DTO enum.
 * Distinct from {@link mapToRestApiLeagueStatus} (list-filter param enum) — the
 * write DTOs carry their own `LeagueStatus` schema.
 */
export const mapToDashboardLeagueStatus = (status: LeagueStatus): DashboardLeagueStatus => {
  switch (status) {
    case "active":
      return DashboardLeagueStatus.active;
    case "inactive":
      return DashboardLeagueStatus.inactive;
  }
};

/** Map the domain geographic scope to the dashboard REST create/update DTO enum. */
export const mapToDashboardLeagueGeoScope = (scope: LeagueGeoScope): DashboardLeagueGeoScope => {
  switch (scope) {
    case "national":
      return DashboardLeagueGeoScope.national;
    case "international":
      return DashboardLeagueGeoScope.international;
    case "local":
      return DashboardLeagueGeoScope.local;
  }
};

/**
 * Map the FE naming variant (`tour` vs `league`) to the backend `TourType`
 * discriminator. A "tour" is `tour`; a "league" is `league`. The two are the
 * same League entity — `tourType` only drives labelling / behaviour.
 */
export const mapToDashboardTourType = (naming: LeagueNaming): TourType => {
  switch (naming) {
    case "tour":
      return TourType.tour;
    case "league":
      return TourType.league;
  }
};

const DOMAIN_TO_DASHBOARD_SOCIAL_MEDIA_TYPE: {
  [key in SocialMediaType]: DashboardSocialMediaType;
} = {
  instagram: DashboardSocialMediaType.instagram,
  facebook: DashboardSocialMediaType.facebook,
  tiktok: DashboardSocialMediaType.tiktok,
  youtube: DashboardSocialMediaType.youtube,
  linkedin: DashboardSocialMediaType.linkedin,
  discord: DashboardSocialMediaType.discord,
  twitch: DashboardSocialMediaType.twitch,
};

/** Map domain social links to the dashboard REST create/update DTO shape. */
export const mapToDashboardLeagueSocialLinks = (
  links: SocialLink[]
): DashboardLeagueSocialLinkInput[] =>
  links.map((link) => ({
    socialMediaType: DOMAIN_TO_DASHBOARD_SOCIAL_MEDIA_TYPE[link.socialMediaType],
    url: link.url,
  }));

export const mapToLeagueReference = (input: LeagueRef): LeagueReference => ({
  id: input.id,
  title: input.title ?? "",
  logo: input.logo ?? null,
});

/**
 * The picker attribution `GET /leagues/search` returns on every row but the
 * checked-in Orval client does not describe yet (its rows are still typed as
 * the slim `LeagueRef`). `yarn rest:codegen` needs a reachable backend, so this
 * type is the **only** place in the app that assumes their wire shape — the
 * read goes through {@link DashboardLeagueSearchItemContract}.
 *
 * Every field is optional on purpose: against a backend that has not shipped
 * them yet the row degrades to a plain reference with no secondary line, rather
 * than rendering `undefined`.
 *
 * TODO(codegen): once `yarn rest:codegen` has been re-run against a backend
 * serving `DashboardLeagueSearchItem`, delete this type and
 * {@link DashboardLeagueSearchItemContract} and take
 * {@link mapToLeagueSearchOption} back to the generated row type.
 */
type DashboardLeagueSearchItemAttribution = {
  status?: DashboardLeagueStatus | null;
  sports?: ApiSportRef[];
  owner?: FederationRef | null;
  scopes?: DashboardScopePair[];
};

/** The single widening point for {@link DashboardLeagueSearchItemAttribution}. */
type DashboardLeagueSearchItemContract = DashboardLeagueRef & DashboardLeagueSearchItemAttribution;

/**
 * Map one row of the league typeahead (`GET /leagues/search`) into the option
 * the league pickers render: the plain reference plus the owner / sports /
 * platform attribution that tells two same-named tours apart, and the lifecycle
 * status that marks a retired one.
 *
 * Each attribution field is read defensively — absent stays absent, so an
 * option coming from the slim refs endpoint and one coming from the search
 * behave identically at the call site.
 */
export const mapToLeagueSearchOption = (
  input: DashboardLeagueSearchItemContract
): LeagueSearchOption => ({
  ...mapToLeagueReference(input),
  status: mapToLeagueStatusNullable(input.status ?? null),
  sports: (input.sports ?? []).map(mapToSportRef),
  owner: input.owner ? mapToOrganizationReference(input.owner) : null,
  scopes: (input.scopes ?? []).map(mapToScopePair),
});

/**
 * Map the backend `tourType` discriminator to the {@link LeagueTourType} domain
 * union. The two enums carry identical string values, but the domain layer stays
 * off the generated enum so a backend drift surfaces here. `null` when unset.
 */
export const mapToLeagueTourTypeNullable = (
  tourType: DashboardTourType | null
): LeagueTourType | null => {
  switch (tourType) {
    case DashboardTourType.tour:
      return "tour";
    case DashboardTourType.circuit:
      return "circuit";
    case DashboardTourType.league:
      return "league";
    case DashboardTourType.series:
      return "series";
    case DashboardTourType.festival:
      return "festival";
    case DashboardTourType.other:
      return "other";
    default:
      return null;
  }
};

export const mapToLeagueListItem = (input: DashboardLeagueListItem): LeagueListItem => ({
  id: input.id,
  internalId: input.internalId,
  title: input.title ?? "",
  type: mapToLeagueGeoScopeNullable(input.type),
  tourType: mapToLeagueTourTypeNullable(input.tourType),
  status: mapToLeagueStatusNullable(input.status),
  country: input.country ? mapToCountryCodeNullable(input.country) : null,
  logo: input.logo ?? null,
  email: input.email ?? null,
  website: input.website ?? null,
  tournamentsCount: input.tournamentsCount,
  sports: (input.sports ?? []).map(mapToSportRef),
  scopes: input.scopes.map(mapToScopePair),
  owner: input.owner ? mapToOrganizationReference(input.owner) : null,
});

const mapToLeagueOverviewNews = (input: DashboardLeagueOverviewNews): LeagueOverviewNews => ({
  id: input.id,
  title: input.title ?? "",
  imageUrl: input.imageUrl ?? null,
  date: new Date(input.date),
  readingMinutes: input.readingMinutes,
  // Mirror the legacy mapper: fall back to the numeric id when no slug exists.
  slug: input.slug ?? String(input.id),
});

const mapToLeagueOverviewAlbum = (input: DashboardLeagueOverviewAlbum): LeagueOverviewAlbum => ({
  id: input.id,
  title: input.title ?? "",
  thumbnailUrl: input.thumbnailUrl ?? null,
  mediaCount: input.mediaCount,
  authorName: input.authorName ?? null,
  createdAt: new Date(input.createdAt),
});

const mapToLeagueOverviewDocument = (
  input: DashboardLeagueOverviewDocument
): LeagueOverviewDocument => ({
  id: input.id,
  name: input.name ?? "",
  documentUrl: input.documentUrl ?? null,
  isPrivate: input.isPrivate,
  updatedAt: new Date(input.updatedAt),
});

const mapToLeagueOverviewPartner = (
  input: DashboardLeagueOverviewPartner
): LeagueOverviewPartner => ({
  id: input.id,
  name: input.name ?? "",
  logoUrl: input.logoUrl ?? null,
  link: input.link ?? null,
});

/**
 * Map the dashboard league overview DTO into the {@link LeagueOverview} domain
 * type. `id` stays the PUBLIC id; `type` / `status` collapse to the same
 * defaults the legacy GraphQL league mapper used (`local` / `inactive`) so the
 * header renders identically.
 *
 * Every collection and the deprecated `internalId` bridge are read defensively:
 * during a mixed-deploy window (new FE against a not-yet-deployed API) fields
 * added to the DTO are simply absent from the response, and a bare `.map()`
 * would throw and take the whole page down over an optional public-view link.
 */
export const mapToLeagueOverview = (input: DashboardLeagueOverview): LeagueOverview => ({
  id: input.id,
  internalId: input.internalId ?? null,
  platform: input.platform ? mapToPlatformReference(input.platform) : null,
  title: input.title ?? "",
  type: mapToLeagueGeoScopeNullable(input.type) ?? "local",
  status: mapToLeagueStatusNullable(input.status) ?? "inactive",
  email: input.email ?? null,
  website: input.website ?? null,
  logo: input.logo ?? null,
  bannerCoverUrl: input.bannerCover ?? null,
  regulationsUrl: input.regulationsUrl ?? null,
  country: input.country ? mapToCountryCodeNullable(input.country) : null,
  athletesCount: input.athletesCount,
  membersCount: input.membersCount,
  news: (input.news ?? []).map(mapToLeagueOverviewNews),
  albums: (input.albums ?? []).map(mapToLeagueOverviewAlbum),
  documents: (input.documents ?? []).map(mapToLeagueOverviewDocument),
  regulations: (input.regulations ?? []).map(mapToLeagueOverviewDocument),
  partners: (input.partners ?? []).map(mapToLeagueOverviewPartner),
});

/**
 * Map the backend `TourType` to the FE naming variant. Only `tour` / `league`
 * have a FE counterpart; every other container kind collapses to `null`.
 * Inverse of {@link mapToDashboardTourType}.
 */
export const mapToLeagueNamingNullable = (
  tourType: DashboardTourType | null
): LeagueNaming | null => {
  switch (tourType) {
    case DashboardTourType.tour:
      return "tour";
    case DashboardTourType.league:
      return "league";
    default:
      return null;
  }
};

// The dashboard REST `SocialMediaType` enum values are identical to the domain
// `SocialMediaType` union, so the inbound map is 1:1.
const DASHBOARD_TO_DOMAIN_SOCIAL_MEDIA_TYPE: {
  [key in DashboardSocialMediaType]: SocialMediaType;
} = {
  [DashboardSocialMediaType.instagram]: "instagram",
  [DashboardSocialMediaType.facebook]: "facebook",
  [DashboardSocialMediaType.tiktok]: "tiktok",
  [DashboardSocialMediaType.youtube]: "youtube",
  [DashboardSocialMediaType.linkedin]: "linkedin",
  [DashboardSocialMediaType.discord]: "discord",
  [DashboardSocialMediaType.twitch]: "twitch",
};

/** Default (all-off) config for a rating system absent from the API payload. */
const EMPTY_LEAGUE_RATING_SYSTEM: LeagueRatingSystem = {
  enabled: false,
  settings: { requireAccount: false, displayRating: false, mandatory: false },
};

const mapToDomainLeagueRatingSystem = (
  input: DashboardLeagueRatingSystems["wtn"] | undefined
): LeagueRatingSystem =>
  input
    ? {
        enabled: input.enabled,
        settings: {
          requireAccount: input.requireAccount,
          displayRating: input.displayRating,
          mandatory: input.mandatory,
        },
      }
    : EMPTY_LEAGUE_RATING_SYSTEM;

/** The three keys the generated `LeagueRatingSystems` DTO declares (and the integrations form renders). */
const KNOWN_LEAGUE_RATING_SYSTEM_KEYS = new Set(["wtn", "dupr", "wpr"]);

/**
 * Narrow an unknown stored `ratingSystems` entry to the flat wire shape. The
 * generated DTO only declares `wtn` / `dupr` / `wpr`, but the backend is adding
 * further systems (`cpc` / `maxwill`) whose keys the checked-in client does not
 * carry yet — their values arrive untyped and are read defensively here.
 */
const isWireLeagueRatingSystem = (
  value: unknown
): value is NonNullable<DashboardLeagueRatingSystems["wtn"]> =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { enabled?: unknown }).enabled === "boolean";

/**
 * Map the REST `LeagueRatingSystems` object (each system optional) into the
 * domain {@link LeagueRatingSystems}, filling all-off defaults for any absent
 * known system so the form always has a complete value to prefill. Every stored
 * entry beyond the three known keys is preserved under `additional` so the
 * integrations-tab save can pass it back verbatim.
 */
const mapToDomainLeagueRatingSystems = (
  input: DashboardLeagueRatingSystems
): LeagueRatingSystems => ({
  wtn: mapToDomainLeagueRatingSystem(input.wtn),
  dupr: mapToDomainLeagueRatingSystem(input.dupr),
  wpr: mapToDomainLeagueRatingSystem(input.wpr),
  additional: Object.fromEntries(
    Object.entries(input)
      .filter(
        ([key, value]) =>
          !KNOWN_LEAGUE_RATING_SYSTEM_KEYS.has(key) && isWireLeagueRatingSystem(value)
      )
      // Raw spread, not a 4-field projection: unknown per-entry fields must
      // survive the integrations-tab round-trip verbatim.
      .map(([key, value]) => [key, { ...value }] as const)
  ),
});

/**
 * Presence-aware inbound map for the tournament-category rating gates: only the
 * systems the league EXPLICITLY stores get a key (their `enabled` flag); absent
 * systems stay absent so the gate can fall back to sport-only for them (a
 * recently created league holds `{}`). Unknown keys (`cpc` / `maxwill` until
 * codegen catches up) are read defensively off the untyped payload.
 */
export const mapToLeagueRatingSystemAvailability = (
  input: DashboardLeagueRatingSystems | null | undefined
): LeagueRatingSystemAvailability => {
  const entries = Object.entries(input ?? {}).flatMap(([key, value]) =>
    isWireLeagueRatingSystem(value) ? [[key, value.enabled] as const] : []
  );
  // An all-false config is indistinguishable from the artifact this admin's own
  // create/save paths write while the integration switches are hard-disabled
  // (no admin can have chosen "everything off" through a disabled UI). Treat it
  // as unconfigured so the category rating gate falls back to sport-only
  // instead of hiding every system.
  if (entries.length > 0 && entries.every(([, enabled]) => !enabled)) return {};
  return Object.fromEntries(entries);
};

/** Map the REST `SporfieMode` enum to the FE domain union. */
const mapToDomainSporfieMode = (mode: SporfieMode): LeagueSporfieMode => {
  switch (mode) {
    case SporfieMode.use_platform_key:
      return "platform";
    case SporfieMode.custom_key:
      return "custom";
    case SporfieMode.disabled:
      return "disabled";
  }
};

/**
 * Normalize the backend's capitalized `fieldType` ("Select" / "Number" /
 * "String") to the lowercase domain union, case-insensitively. Anything
 * unexpected — including a missing/`null` `fieldType`, which legacy "String"
 * fact-sheet rows are stored with — falls back to `"string"`.
 */
const mapToLeagueFactSheetFieldKind = (
  fieldType: string | null | undefined
): LeagueFactSheetFieldKind => {
  switch (fieldType?.toLowerCase()) {
    case "select":
      return "select";
    case "number":
      return "number";
    default:
      return "string";
  }
};

/**
 * Safely parse a fact-sheet field's raw `options` JSON string into the domain
 * `{ title }[]` shape. The backend stores `[{ "title": "..." }]` for `Select`
 * fields (and a single blank entry for `Number` / `String`). Never throws:
 * returns `[]` on a parse failure or a non-array, and keeps only entries with a
 * string `title`.
 */
const parseLeagueFactSheetOptions = (options: string | null | undefined): { title: string }[] => {
  if (!options) return [];

  try {
    const parsed: unknown = JSON.parse(options);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(
        (entry): entry is { title: string } =>
          typeof entry === "object" &&
          entry !== null &&
          "title" in entry &&
          typeof (entry as { title: unknown }).title === "string"
      )
      .map((entry) => ({ title: entry.title }));
  } catch {
    return [];
  }
};

/**
 * Map the league detail DTO's fact-sheet rows into the {@link LeagueFactSheetField}
 * domain shape consumed by the Fact sheet form. Preserves order (the backend
 * already sorts by `sortingPosition`). Never throws on malformed `options`.
 */
export const mapToLeagueFactSheet = (
  items: DashboardLeagueFactSheetItem[] | null | undefined
): LeagueFactSheetField[] => {
  if (!items) return [];

  return items.map((item) => ({
    id: item.id,
    type: mapToLeagueFactSheetFieldKind(item.fieldType),
    title: item.fieldTitle ?? "",
    options: parseLeagueFactSheetOptions(item.options),
  }));
};

/**
 * Map the league detail's flat `customFees` rows into the ENTRY-only
 * `FeesSegment` list the fees-tab prefill consumes. Leagues have no license fees,
 * so any non-ENTRY rows are filtered out defensively (the BE already returns only
 * ENTRY rows). Each row maps through the shared {@link mapToFeesSegment}.
 */
const mapToLeagueCustomFees = (rows: DashboardPlatformFee[]): LeagueDetail["customFees"] =>
  rows.map(mapToFeesSegment).filter((segment) => segment.serviceType === "entry");

/**
 * Map the dashboard league detail DTO (`GET /leagues/:id`) into the
 * {@link LeagueDetail} domain type consumed by the update-league page/forms.
 * Mirrors {@link mapToLeagueOverview}'s style and reuses the shared enum / ref /
 * country mappers. `discipline` stays the raw stored id (see `LeagueDetail`).
 */
/**
 * The checked-in Orval client predates the per-kind tagged-content settings —
 * see `WithTaggedContentSettings` in `contentLink.mappers.ts` for the single
 * contract assumption and its `TODO(codegen)`.
 */
type DashboardLeagueDetailContract = WithTaggedContentSettings<DashboardLeagueDetail>;

export const mapToLeagueDetail = (input: DashboardLeagueDetailContract): LeagueDetail => ({
  id: input.id,
  title: input.title ?? "",
  abbreviation: input.abbreviation ?? null,
  type: mapToLeagueGeoScopeNullable(input.type),
  tourType: mapToLeagueNamingNullable(input.tourType),
  status: mapToLeagueStatusNullable(input.status),
  country: input.country ? mapToCountryCodeNullable(input.country) : null,
  sports: (input.sports ?? []).map(mapToSportRef),
  discipline: input.discipline
    ? { id: input.discipline.id, title: input.discipline.title ?? "" }
    : null,
  logo: input.logo ?? null,
  bannerCover: input.bannerCover ?? null,
  abbreviationLogo: input.abbreviationLogo ?? null,
  email: input.email ?? null,
  phone: input.phone ?? null,
  website: input.website ?? null,
  socialLinks: input.socialLinks.map((link) => ({
    socialMediaType: DASHBOARD_TO_DOMAIN_SOCIAL_MEDIA_TYPE[link.socialMediaType],
    url: link.url,
  })),
  clubs: input.clubs.map(mapToClubReference),
  isFeatured: input.isFeatured,
  requireTournamentApproval: input.requireTournamentApproval,
  allowUnregisteredPlayers: input.allowUnregisteredPlayers,
  enableRankings: input.enableRankings ?? false,
  enableFactSheet: input.enableFactSheet ?? false,
  ...mapToTaggedContentSettings(input),
  globalCategoryLimitPerPlayer: input.globalCategoryLimitPerPlayer ?? null,
  owner: input.owner ? mapToOrganizationReference(input.owner) : null,
  ratingSystems: mapToDomainLeagueRatingSystems(input.ratingSystems),
  sporfieMode: mapToDomainSporfieMode(input.sporfieMode),
  sporfieKey: input.sporfieKey ?? null,
  factSheet: mapToLeagueFactSheet(input.factSheet),
  isCustomFeeEnable: input.isCustomFeeEnable,
  customFees: mapToLeagueCustomFees(input.customFees),
  tournatedFeeVat: mapToTournatedFeeVat(input),
  inheritedTournatedFeeVat: mapToInheritedTournatedFeeVat(input.inheritedTournatedFeeVat),
  capabilities: mapToCapabilitySet(input.capabilities),
  ...mapToLeagueWithDocumentsFromApi(input),
});

/**
 * Map the league detail DTO's two independent documents collections
 * (`documents` and `regulations`, each with their folders) into the
 * entity-agnostic {@link LeagueWithDocuments} shape the shared documents manager
 * consumes. Mirrors `mapToOrganizationWithDocumentsFromApi`; the league /
 * federation / platform document DTOs are structurally identical, so the shared
 * REST mapper handles all three — here it runs once per collection.
 */
export const mapToLeagueWithDocumentsFromApi = (
  detail: DashboardLeagueDetail
): LeagueWithDocuments => {
  const documents = mapToDocumentsAndFoldersFromApi({
    documents: detail.documents,
    documentFolders: detail.documentFolders,
  });
  const regulations = mapToDocumentsAndFoldersFromApi({
    documents: detail.regulations,
    documentFolders: detail.regulationFolders,
  });

  return {
    documents: documents.documents,
    documentFolders: documents.documentFolders,
    regulations: regulations.documents,
    regulationFolders: regulations.documentFolders,
  };
};

export const mapToLeagueOnboardingStep = (
  input: DashboardLeagueOnboardingStep
): LeagueOnboardingStepState => ({
  step: input.step,
  isCompleted: input.isCompleted,
  completedAt: input.completedAt,
});

export const mapToLeagueAthleteListItem = (
  item: DashboardLeagueAthleteListItem
): LeagueAthleteListItem => ({
  id: item.id,
  internalId: item.internalId,
  tournatedTag: item.tournatedTag,
  name: item.name ?? "",
  surname: item.surname ?? "",
  ...mapAvatarFields(item),
  nation: item.nation ? mapToCountryCodeNullable(item.nation) : null,
  birthDate: item.birthDate ? fromApiCalendarDateNullable(item.birthDate) : null,
  sports: item.sports.map(mapToSportRef),
  clubs: item.clubs.map(mapToClubReferenceFromRef),
  tournaments: item.tournaments,
});

/**
 * Map the dashboard league-teams list-item DTO (`GET /leagues/:id/teams`) into
 * the {@link LeagueTeamListItem} row model. `sport` / `club` are single &
 * nullable (guard nulls before the ref mappers); `tournamentsCount` is already
 * scoped to this league by the backend.
 */
export const mapToLeagueTeamListItem = (item: DashboardLeagueTeamListItem): LeagueTeamListItem => ({
  id: item.id,
  internalId: item.internalId,
  title: item.title ?? "",
  image: item.image ?? null,
  country: item.country ? mapToCountryCodeNullable(item.country) : null,
  email: item.email ?? null,
  phone: item.phone ?? null,
  sport: item.sport ? mapToSportRef(item.sport) : null,
  club: item.club ? mapToClubReference(item.club) : null,
  membersCount: item.membersCount,
  tournamentsCount: item.tournamentsCount,
});

// The generated role enums (list-item / create / update / list-filter) all carry
// the same three string values as the domain `LeagueMemberRoleName` union, but we
// keep the domain layer off the generated enums. Explicit maps let the compiler
// flag a drift the moment the backend adds a role.
const LIST_ITEM_ROLE_TO_DOMAIN: {
  [K in DashboardLeagueMemberListItemRolesItem]: LeagueMemberRoleName;
} = {
  [DashboardLeagueMemberListItemRolesItem.organizer]: "organizer",
  [DashboardLeagueMemberListItemRolesItem.manager]: "manager",
  [DashboardLeagueMemberListItemRolesItem.referee]: "referee",
};

const DOMAIN_ROLE_TO_CREATE: {
  [K in LeagueMemberRoleName]: DashboardCreateLeagueMemberInputRolesItem;
} = {
  organizer: DashboardCreateLeagueMemberInputRolesItem.organizer,
  manager: DashboardCreateLeagueMemberInputRolesItem.manager,
  referee: DashboardCreateLeagueMemberInputRolesItem.referee,
};

const DOMAIN_ROLE_TO_UPDATE: {
  [K in LeagueMemberRoleName]: DashboardUpdateLeagueMemberInputRolesItem;
} = {
  organizer: DashboardUpdateLeagueMemberInputRolesItem.organizer,
  manager: DashboardUpdateLeagueMemberInputRolesItem.manager,
  referee: DashboardUpdateLeagueMemberInputRolesItem.referee,
};

const DOMAIN_ROLE_TO_FILTER: {
  [K in LeagueMemberRoleName]: DashboardLeagueMembersControllerListRolesItem;
} = {
  organizer: DashboardLeagueMembersControllerListRolesItem.organizer,
  manager: DashboardLeagueMembersControllerListRolesItem.manager,
  referee: DashboardLeagueMembersControllerListRolesItem.referee,
};

/** Map a list-item role enum to the FE role union. */
export const mapToLeagueMemberRoleName = (
  role: DashboardLeagueMemberListItemRolesItem
): LeagueMemberRoleName => LIST_ITEM_ROLE_TO_DOMAIN[role];

/** Map a FE role to the create DTO enum. */
export const mapToCreateLeagueMemberRole = (
  role: LeagueMemberRoleName
): DashboardCreateLeagueMemberInputRolesItem => DOMAIN_ROLE_TO_CREATE[role];

/** Map a FE role to the update DTO enum (full replace set). */
export const mapToUpdateLeagueMemberRole = (
  role: LeagueMemberRoleName
): DashboardUpdateLeagueMemberInputRolesItem => DOMAIN_ROLE_TO_UPDATE[role];

/** Map a FE role to the list-filter enum. */
export const mapToLeagueMemberFilterRole = (
  role: LeagueMemberRoleName
): DashboardLeagueMembersControllerListRolesItem => DOMAIN_ROLE_TO_FILTER[role];

/**
 * The two server-resolved public contact channels the backend returns on
 * `GET /leagues/:id/members` rows but the checked-in Orval client does not
 * describe yet. `yarn rest:codegen` needs a reachable backend, so this type is
 * the **only** place in the app that assumes their wire shape — the read
 * (`mapToLeagueManagementMember`) goes through
 * {@link DashboardLeagueMemberListItemContract}.
 *
 * Both fields are optional on purpose: against a backend that has not shipped
 * them yet the reads fall back to `null`, i.e. no contact column content.
 *
 * TODO(codegen): once `yarn rest:codegen` has been re-run against a backend
 * exposing `effectiveContactEmail` and `effectiveContactPhone` on
 * `DashboardLeagueMemberListItem`, delete this type and
 * `DashboardLeagueMemberListItemContract`, take `mapToLeagueManagementMember`
 * back to a plain `DashboardLeagueMemberListItem` parameter, and drop the
 * `?? null` fallbacks on the two `effectiveContact…` fields below.
 */
type DashboardLeagueMemberEffectiveContact = {
  effectiveContactEmail?: string | null;
  effectiveContactPhone?: string | null;
};

/** The single widening point for {@link DashboardLeagueMemberEffectiveContact}. */
type DashboardLeagueMemberListItemContract = DashboardLeagueMemberListItem &
  DashboardLeagueMemberEffectiveContact;

/**
 * Map the dashboard league-member list-item DTO
 * (`GET /leagues/:id/members`) into the {@link LeagueManagementMember} row model
 * consumed by the league edit page's "Team" tab. Avatar fields pass through raw
 * (resolved at the render site); roles bridge to the FE role union. The
 * `effectiveContact…` pair is read defensively — missing means `null`.
 */
export const mapToLeagueManagementMember = (
  item: DashboardLeagueMemberListItemContract
): LeagueManagementMember => ({
  userId: item.userId,
  name: item.name ?? "",
  surname: item.surname ?? "",
  email: item.email ?? "",
  ...mapAvatarFields(item),
  roles: item.roles.map(mapToLeagueMemberRoleName),
  tournamentsCount: item.tournamentsCount ?? 0,
  isOrganization: item.isOrganization ?? null,
  organizationName: item.organizationName ?? null,
  organizationEmail: item.organizationEmail ?? null,
  organizationPhone: item.organizationPhone ?? null,
  organizationLogo: item.organizationLogo
    ? { fileId: item.organizationLogo.fileId, url: item.organizationLogo.url ?? null }
    : null,
  contactEmail: item.contactEmail ?? null,
  contactPhone: item.contactPhone ?? null,
  effectiveContactEmail: item.effectiveContactEmail ?? null,
  effectiveContactPhone: item.effectiveContactPhone ?? null,
  displayMember: item.displayMember ?? null,
});
